import { Check } from "lucide-react";
import { Reveal } from "@/components/MaskedLines";

const SERVICES = [
  {
    id: "service-card-pattern",
    tag: "Development",
    title: "Pattern Making & Sampling",
    desc: "From sketch or reference sample to production-ready patterns, graded across your size run.",
    bullets: ["Pattern drafting & grading", "Sample development before bulk", "Fit revisions with your team"],
    img: "https://images.unsplash.com/photo-1606501126768-b78d4569d3f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxnYXJtZW50JTIwbWFudWZhY3R1cmluZyUyMHNld2luZyUyMGZhYnJpYyUyMHRleHRpbGUlMjBhdGVsaWVyfGVufDB8fHx8MTc4OTA0NjYwMHww&ixlib=rb-4.1.0&q=85",
    alt: "Artisan sewing a garment detail",
  },
  {
    id: "service-card-bulk",
    tag: "Production",
    title: "Bulk Apparel Production",
    desc: "Structured cut-and-sew lines for repeat orders and full production runs, scheduled with you.",
    bullets: ["Woven & knit tops", "Resort wear and uniforms", "Made-to-order production runs"],
    img: "https://images.unsplash.com/photo-1560796952-f1c9b838544c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxnYXJtZW50JTIwbWFudWZhY3R1cmluZyUyMHNld2luZyUyMGZhYnJpYyUyMHRleHRpbGUlMjBhdGVsaWVyfGVufDB8fHx8MTc4OTA0NjYwMHww&ixlib=rb-4.1.0&q=85",
    alt: "Industrial sewing machine stitch detail",
  },
  {
    id: "service-card-sourcing",
    tag: "Materials",
    title: "Fabric Sourcing & Inspection",
    desc: "Textile sourcing through Bali and Indonesian suppliers, with quality checks at every stage.",
    bullets: ["Fabric & trim sourcing", "Dyeing and finishing coordination", "Inline and final inspection"],
    img: "https://images.unsplash.com/photo-1517146783983-418c681b56c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxnYXJtZW50JTIwbWFudWZhY3R1cmluZyUyMHNld2luZyUyMGZhYnJpYyUyMHRleHRpbGUlMjBhdGVsaWVyfGVufDB8fHx8MTc4OTA0NjYwMHww&ixlib=rb-4.1.0&q=85",
    alt: "Assorted colour thread spools",
  },
  {
    id: "service-card-finishing",
    tag: "Delivery",
    title: "Finishing & Export Packing",
    desc: "Trimming, pressing, labelling and export-ready packing before every shipment leaves Gianyar.",
    bullets: ["Thread trimming & pressing", "Labels, hang tags & polybags", "Carton packing for export"],
    img: "https://images.unsplash.com/photo-1578353022142-09264fd64295?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxnYXJtZW50JTIwbWFudWZhY3R1cmluZyUyMHNld2luZyUyMGZhYnJpYyUyMHRleHRpbGUlMjBhdGVsaWVyfGVufDB8fHx8MTc4OTA0NjYwMHww&ixlib=rb-4.1.0&q=85",
    alt: "Measuring tape, shears and thread spools",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-6xl px-5 sm:px-6">
      <Reveal className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#86868b]">Manufacturing Services</p>
        <h2
          data-testid="services-heading"
          className="mt-4 text-3xl font-semibold leading-[1.12] tracking-tight text-[#1d1d1f] sm:text-4xl lg:text-5xl"
        >
          Everything a garment needs. <span className="text-[#86868b]">Under one roof.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#86868b]">
          Bring us a tech pack, a sample, or only an idea — we will tell you honestly what we can make.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 sm:mt-16 md:grid-cols-2">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={0.06 * i}>
            <article
              data-testid={s.id}
              className="group h-full overflow-hidden rounded-[28px] bg-[#f5f5f7] transition-transform duration-500 ease-out hover:scale-[1.015]"
            >
              <div className="overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-8 sm:p-10">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0066cc]">{s.tag}</p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-[#1d1d1f] sm:text-3xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#86868b] sm:text-base">{s.desc}</p>
                <ul className="mt-6 flex flex-col gap-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-[#1d1d1f]/80">
                      <Check size={15} className="shrink-0 text-[#0066cc]" strokeWidth={2.5} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
